import Source from '../sources/Source';
import SourceTextCache from '../sources/SourceTextCache';
import VersionRenderer from './VersionRenderer';
import VersionRenderStyle from './VersionRenderStyle';
import VersionStyles from './VersionStyles';

export interface StyleRule {
    readonly equals?: string;
    readonly startsWith?: string;
    readonly styles: string[];
}

export interface StyleSource {
    readonly source: Source;
    readonly sourceFormatter?: (text: string) => string;
    readonly styleRules: StyleRule[];
}

interface StyleSourceWithParts {
    readonly source: StyleSource;
    readonly parts: string[];
}

export default class StyledRenderer implements VersionRenderer {

    constructor(private readonly displaySource: Source, private readonly styleSources: StyleSource[],
        private readonly unstyledWords: string[]) {
    }

    public render(verseIndex: number, sourceTextCache: SourceTextCache, style?: VersionRenderStyle): string {
        const versionStyle = VersionStyles.mergeStyles(VersionStyles.default(), style || {});

        const verseText = sourceTextCache.getText(verseIndex, this.displaySource.getIndex());
        if (verseText === undefined) {
            return '';
        }

        let text;
        const classes = [];

        if (verseText !== '') {
            const styleSources: StyleSourceWithParts[] = this.styleSources.map(styleSource => {
                const sourceText = sourceTextCache.getText(verseIndex, styleSource.source.getIndex());
                const formattedText = sourceText && styleSource.sourceFormatter ? styleSource.sourceFormatter(sourceText) : sourceText;
                const sourceParts = formattedText ? formattedText.split(' ') : [];
                return {
                    source: styleSource,
                    parts: sourceParts
                };
            });

            // Splitting by % will leave spaces so trim each part and filter out any empty ones.
            const parts = verseText.split('%').map(part => part.trim()).filter(part => part !== '');
            const renderedParts = this.renderParts(parts, styleSources);
            text = renderedParts.join(' ');

            if (versionStyle.classes) {
                classes.push(...versionStyle.classes);
            }
        } else {
            text = 'Omitted';
            classes.push('_Omitted');
        }

        const classAttr = classes.length > 0 ? ` class="${classes.join(' ')}"` : '';
        return `<div${classAttr}>${text}</div>`;
    }

    private renderParts(parts: string[], styleSources: StyleSourceWithParts[]): string[] {
        return parts.map((part, index) => {
            return part
                .split(' ') // Split part into words.
                .map(word => {
                    if (this.unstyledWords.indexOf(word) !== -1) {
                        return word; // Do not style this word.
                    }

                    const styles: string[] = [];

                    for (const styleSource of styleSources) {
                        if (index < styleSource.parts.length) {
                            // Style source has a part in this position.
                            const sourcePart = styleSource.parts[index];
                            styles.push(...this.getTextStyles(sourcePart, styleSource.source.styleRules));
                        }
                    }

                    const styleAttr = styles.length > 0 ? ` style="${styles.join(';')}"` : '';
                    return `<span${styleAttr}>${word}</span>`;
                })
                .join(' '); // Join words back into part.
        });
    }

    private getTextStyles(text: string, rules: StyleRule[]): string[] {
        const styles = rules.filter(rule => {
            if (rule.equals) {
                return text === rule.equals;
            } else if (rule.startsWith) {
                return text.startsWith(rule.startsWith);
            } else {
                return false;
            }
        }).map(rule => rule.styles);
        return styles.reduce((all, next) => all.concat(next), []); // Flatten map.
    }
}
